import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import datapoints from '../datapoints';
import getFirstBody from '../utils/getFirstBody';
import getQuotedTextList from '../utils/getQuotedTextList';
import getTranslationOptOut from 'owa-translation/lib/utils/getTranslationOptOut';
import getTranslationContentType from '../utils/getTranslationContentType';
import * as trace from 'owa-trace';
import { TranslationContentType } from '../utils/TranslationContentType';
import StoredTranslationData from '../store/schema/StoredTranslationData';
import storeTranslationData from '../services/storeTranslationData';
import storeTranslationStatus from '../services/storeTranslationStatus';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { ClientItem, mailStore } from 'owa-mail-store';
import { DatapointStatus, ActionDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetTargetLanguageTag, lazyTranslateText } from 'owa-translation';
import { lazyLogFloodgateActivity } from 'owa-floodgate-feedback-view';
import { ClientType } from '../utils/clientType';
import { EmailTranslationMetricsMessageBucket } from '../utils/MessageBucketEnum';
import TranslationType from 'owa-translation/lib/store/schema/TranslationType';
import { getEntityIdForCurrentItem } from '../services/getLocalePropertyForItem';

export default createLegacyActionWithDatapoint(
    'translateItem',
    datapoints.InlineTranslationMessageTranslated
)(async function translateItem(
    itemId: string,
    subject: string,
    isConversationItemPart: boolean = false,
    manualTranslation: boolean = false,
    autoTranslation: boolean = false
) {
    const datapoint: ActionDatapoint = returnTopExecutingActionDatapoint();
    const getTargetLanguageTag = await lazyGetTargetLanguageTag.import();
    const floodgateSurveyDelay = 7000;
    const item: ClientItem = mailStore.items.get(itemId);
    let translated = false;

    let conversationItemParts: ConversationItemParts;
    if (isConversationItemPart) {
        conversationItemParts = mailStore.conversations.get(item.ConversationId.Id);
    }

    item.TranslationData.correlationId = getGuid();
    item.TranslationData.isTranslating = true;
    item.TranslationData.targetLanguageTag = getTargetLanguageTag();
    item.TranslationData.translationFailed = false;

    let translateSubject = false;
    let translateBody = false;
    let translateForward = false;
    let itemTexts: string[] = [];

    let isSubjectTextTranslated = isSubjectTranslated(
        conversationItemParts,
        item,
        isConversationItemPart
    );

    if (subject && !isSubjectTextTranslated) {
        // Push the subject into the end of the array.
        itemTexts.push(subject);
        translateSubject = true;
    }

    if (
        item.TranslationData.translationText == null &&
        (item.TranslationData.isTranslatable || manualTranslation)
    ) {
        if (!getTranslationOptOut(item.TranslationData.itemOgmaLocale) || manualTranslation) {
            let firstBodyText: string = getFirstBody(item, isConversationItemPart);
            if (firstBodyText) {
                itemTexts.push(firstBodyText);
                translateBody = true;
            }
        }
    }

    if (isConversationItemPart) {
        const quotedTextList: string[] = getQuotedTextList(item);
        if (
            quotedTextList &&
            ((item.TranslationData.forwardContentTranslationText === null &&
                item.TranslationData.isForwardContentTranslatable &&
                !getTranslationOptOut(item.TranslationData.forwardContentLocale)) ||
                manualTranslation)
        ) {
            // quotedTextList is an Observable and can't use .concat() with itemText.
            // Push each forward content to the end of the array.
            quotedTextList.forEach(quotedText => {
                if (quotedText) {
                    itemTexts.push(quotedText);
                }
            });
            translateForward = true;
        }
    }

    // Call Ogma service to translate the item.
    // Values are returned in the same order as they were stored in the array.
    if (translateSubject || translateBody || translateForward) {
        let translatedItemTexts: string[];
        try {
            const translateText = await lazyTranslateText.import();
            const translationType = getTranslationType(manualTranslation, autoTranslation);
            translatedItemTexts = await translateText(
                itemTexts,
                item.TranslationData.targetLanguageTag,
                item.TranslationData.correlationId,
                translationType
            );
            translated = true;
        } catch (ex) {
            handleTranslationError(item, ex, datapoint, item.TranslationData.correlationId);
            return;
        }

        if (isFeatureEnabled('tra-logEntityType')) {
            const entityId: string = getEntityIdForCurrentItem();
            if (datapoint && entityId) {
                datapoint.addCustomData({ EntityId: entityId });
            }
        }

        // We only ask for feedback during the initial session translation was done,
        // In future sessions it is loaded from cache so this won't run.
        item.TranslationData.shouldGetFeedback = true;

        if (translateSubject) {
            // Pops the subject from the beginning of the array.
            const subject = translatedItemTexts.shift();
            if (isConversationItemPart) {
                conversationItemParts.subjectTranslationData.translationText = subject;
            } else {
                item.TranslationData.subjectTranslationText = subject;
            }

            isSubjectTextTranslated = true;
        }
        if (translateBody) {
            // Pops the body from the beginning of the array.
            const body = translatedItemTexts.shift();
            item.TranslationData.translationText = body;
        }
        if (translateForward) {
            item.TranslationData.forwardContentTranslationText = translatedItemTexts;
        }
    }

    item.TranslationData.isTranslating = false;

    let updateStatus = false;
    if (
        (item.TranslationData.translationText ||
            item.TranslationData.forwardContentTranslationText) &&
        (isSubjectTextTranslated || !subject)
    ) {
        if (
            isFeatureEnabled('tra-cacheTranslation') &&
            !item.TranslationData.isShowingTranslation &&
            !item.TranslationData.isShowingForwardContentTranslation
        ) {
            updateStatus = true;
        }

        if (item.TranslationData.translationText !== null) {
            item.TranslationData.isShowingTranslation = true;
        }
        if (item.TranslationData.forwardContentTranslationText !== null) {
            item.TranslationData.isShowingForwardContentTranslation = true;
        }
        if (subject !== null) {
            if (isConversationItemPart) {
                conversationItemParts.subjectTranslationData.isShowingTranslation = true;
            } else {
                item.TranslationData.isShowingSubjectTranslation = true;
            }
        }

        // set flag if the translation was done in conversation view off.
        if (!isConversationItemPart) {
            if (datapoint) {
                datapoint.addCustomData({ conversationViewOff: true });
            }
        }

        // set flag if this is invoked through manual entry point, and suggestion would not be otherwise shown
        if (
            item.TranslationData.manuallyTranslated ||
            (manualTranslation && !isTranslationSuggestionShowing(item))
        ) {
            if (datapoint) {
                datapoint.addCustomData({ manual: true });
            }
            item.TranslationData.manuallyTranslated = true;
        }

        // set flag if this was automatic translation
        if (autoTranslation) {
            if (datapoint) {
                datapoint.addCustomData({ auto: true });
            }
        }

        // Set flag if the language was detected using FastText
        if (item.TranslationData.isFastTextLanguage && datapoint) {
            datapoint.addCustomData({ fastText: true });
        }

        if (isFeatureEnabled('rp-inlineTranslationReportMessageBucketLength')) {
            if (translateSubject || translateBody || translateForward) {
                // We only log the mail size bucket when we translated and not for cache retrieval scenarios.
                const messageBucket: EmailTranslationMetricsMessageBucket = getMailBucketFromMailLength(
                    itemTexts
                );
                if (datapoint) {
                    datapoint.addCustomData({ messageBucket: messageBucket });
                }
            }
        }
    }

    if (isFeatureEnabled('rp-inlineTranslation-floodgateSurvey')) {
        setTimeout(function () {
            lazyLogFloodgateActivity.import().then(logFloodgateActivity => {
                logFloodgateActivity('TranslationCompleted');
            });
        }, floodgateSurveyDelay);
    }

    // Do not cache translation for rights protected items
    if (
        isFeatureEnabled('tra-cacheTranslation') &&
        translated &&
        !item.RightsManagementLicenseData
    ) {
        const subject = isConversationItemPart
            ? conversationItemParts.subjectTranslationData.translationText
            : item.TranslationData.subjectTranslationText;
        const data: StoredTranslationData = {
            language: item.TranslationData.targetLanguageTag,
            subject: subject,
            body: item.TranslationData.translationText,
            forward: item.TranslationData.forwardContentTranslationText,
            isConversation: isConversationItemPart,
            contentType: getTranslationContentType(item, isConversationItemPart),
            clientType: ClientType.OWA,
            dateTime: Date.now(),
        };

        if (data.contentType === TranslationContentType.Undefined) {
            trace.errorThatWillCauseAlert(
                '[InlineTranslation] CacheTranslation ContentType Unknown'
            );
        } else {
            await storeTranslationData(item.ItemId.Id, data);
        }
    }
    if (updateStatus) {
        await storeTranslationStatus(item.ItemId.Id, true);
    }
});

function isTranslationSuggestionShowing(item: ClientItem) {
    return (
        (item.TranslationData.isTranslatable &&
            !getTranslationOptOut(item.TranslationData.itemOgmaLocale)) ||
        (item.TranslationData.isForwardContentTranslatable &&
            !getTranslationOptOut(item.TranslationData.forwardContentLocale))
    );
}

function isSubjectTranslated(
    conversationItemParts: ConversationItemParts,
    item: ClientItem,
    isConversationItemPart: boolean
): boolean {
    if (isConversationItemPart && conversationItemParts.subjectTranslationData.translationText) {
        return true;
    } else if (!isConversationItemPart && item.TranslationData.subjectTranslationText) {
        return true;
    } else {
        return false;
    }
}

function handleTranslationError(item: ClientItem, ex: string, datapoint, correlationId: string) {
    item.TranslationData.isTranslating = false;
    item.TranslationData.translationFailed = true;
    if (datapoint) {
        const error = new Error(
            '[InlineTranslation] TranslateText failed. Error:' + ex + ' | cid:' + correlationId
        );
        datapoint.endWithError(DatapointStatus.ServerError, error);
    }
}

function getMailBucketFromMailLength(itemTexts: string[]): EmailTranslationMetricsMessageBucket {
    if (!itemTexts) {
        return EmailTranslationMetricsMessageBucket.ErrorOrUnexpectedValue;
    }

    let mailLength: number = 0;

    for (var i = 0; i < itemTexts.length; i++) {
        if (mailLength > 0 && itemTexts[i].length > Number.MAX_VALUE - mailLength) {
            return EmailTranslationMetricsMessageBucket.MaxValue;
        } else {
            mailLength = mailLength + itemTexts[i].length;
        }
    }

    switch (mailLength >= 0) {
        case mailLength >= 0 && mailLength < 5000:
            return EmailTranslationMetricsMessageBucket.ZeroToFiveThousand;
        case mailLength >= 5000 && mailLength < 10000:
            return EmailTranslationMetricsMessageBucket.FiveToTenThousand;
        case mailLength >= 10000 && mailLength < 100000:
            return EmailTranslationMetricsMessageBucket.TenToOneHundredThousand;
        case mailLength >= 100000 && mailLength < 200000:
            return EmailTranslationMetricsMessageBucket.OneHundredToTwoHundredThousand;
        case mailLength >= 200000:
            return EmailTranslationMetricsMessageBucket.MaxValue;
        default:
            return EmailTranslationMetricsMessageBucket.ErrorOrUnexpectedValue;
    }
}

function getTranslationType(manualTranslation: boolean, autoTranslation: boolean): TranslationType {
    if (manualTranslation) {
        return TranslationType.Manual;
    }
    if (autoTranslation) {
        return TranslationType.Automatic;
    }
    return TranslationType.Suggestion;
}
